/* =========================================================================
   Served-question logging → POST /api/log/served. Each time a question is
   SERVED in the controller we record its stable code (stableCodeFor). Codes are
   batch-posted (Bearer when authed, else the guest id) at round completion, game
   completion, and on visibilitychange→hidden (keepalive). Codes are deduped for
   the lifetime of a game; failures requeue once, fire-and-forget — this is
   telemetry, never gameplay-blocking. sessionId is null (client sessions aren't
   server-driven yet).
   ========================================================================= */
import { stableCodeFor } from './data/questionCodes';
import { postQuestionsServed, beaconQuestionsServed, isAuthenticated } from './api';

const served = new Set<string>(); // every code served this game (dedupe)
let pending: string[] = []; // codes recorded but not yet posted

/** Start a fresh served-set for a new game. */
export function resetServedLog(): void {
  served.clear();
  pending = [];
}

/** Record a served question by its client id (mapped to a stable code). */
export function recordServed(id: number): void {
  recordServedCode(stableCodeFor(id));
}

/** Record a served question by its stable code directly — server-served questions
    (POST /api/game/round-questions) already carry the authoritative code, and new
    server-authored content has no bundled client id to map from. */
export function recordServedCode(code: string): void {
  if (!code) return;
  if (served.has(code)) return; // already served this game
  served.add(code);
  pending.push(code);
}

/** Post any pending codes. Requeues the batch on failure (retried next flush). */
export async function flushServed(useKeepalive = false): Promise<void> {
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];
  const ok = await postQuestionsServed(batch, useKeepalive);
  if (!ok) pending = batch.concat(pending); // fire-and-forget: try again next flush
}

/** Teardown flush shared by pagehide + visibilitychange→hidden. Guests go out
    via sendBeacon (survives an immediate PWA kill; take-then-requeue on refusal,
    then fall back to fetch keepalive); authed users use fetch keepalive so the
    Bearer token rides along. Double-fire (hidden then pagehide) is safe — the
    second call sees an empty `pending` and returns. No persistence for served
    codes (dedupe is per-game and rebuilt on the next play). */
function flushServedOnHide(): void {
  if (pending.length === 0) return;
  if (!isAuthenticated()) {
    const batch = pending;
    pending = [];
    const sent = beaconQuestionsServed(batch);
    if (!sent) {
      pending = batch.concat(pending); // beacon refused → retry via fetch keepalive
      void flushServed(true);
    }
    return;
  }
  void flushServed(true);
}

/** Wire the page-hide keepalive flush once at boot. pagehide AND
    visibilitychange are both wired: iOS/PWA kills often deliver only one. */
export function initServedLog(): void {
  window.addEventListener('pagehide', flushServedOnHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushServedOnHide();
  });
}
